import { endpointFor } from "@delegate/shared/tools";
import {
  DELEGATE_TOOL_NAME,
  type DebugEvent,
  type DebugEventType,
  type DelegateResult,
  type Status,
} from "@delegate/shared";
import { RealtimeFunctionCallLoop, type RealtimeTransport } from "@delegate/shared/realtime-loop";
import { getAvatarId } from "./api.ts";

/**
 * How many times the startup briefing check may be asked for.
 *
 * Greeting the avatar immediately cancels the first attempt, so one retry is
 * the common path. After this it is abandoned quietly - the check is
 * instrumentation, and it must never take the turn away from the user.
 */
const BRIEFING_MAX_ATTEMPTS = 3;

export type TranscriptEntry = { role: "user" | "assistant"; text: string; timestamp: string };

export type CharterSection = { id: string; title: string; body: string };
export type CharterView = {
  sections: CharterSection[];
  tools: { id: string; summary: string; usage?: string; docsUrl?: string }[];
};

export type RealtimeCallbacks = {
  onStatus: (s: Status) => void;
  onCharter?: (c: CharterView) => void;
  /** undefined while pending, then the model's own words, or null if it failed. */
  onBriefingAck?: (ack: string | null) => void;
  onCost?: (sessionUsd: number) => void;
  onDebug: (e: DebugEvent) => void;
  onTranscript: (t: TranscriptEntry) => void;
  onError: (message: string) => void;
};

const OPENAI_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

const avatarHeader = (): Record<string, string> => {
  const id = getAvatarId();
  return id ? { "x-avatar-id": id } : {};
};

export class RealtimeSessionClient {
  private pc?: RTCPeerConnection;
  private dc?: RTCDataChannel;
  private mic?: MediaStream;
  private audioEl?: HTMLAudioElement;
  private loop?: RealtimeFunctionCallLoop;
  private openaiSessionId?: string;
  private audioAnnouncedFor = new Set<string>();
  private awaitingBriefingAck = false;
  /** Which response carries the briefing answer, so an interruption is attributable. */
  private briefingResponseId: string | null = null;
  private briefingAttempts = 0;
  private briefingTimer: ReturnType<typeof setTimeout> | null = null;
  private userIsSpeaking = false;
  /** A response already in flight; starting another is an API error. */
  private activeResponseId: string | null = null;
  private connectStartedAt = 0;

  readonly localSessionId: string;
  /** Reported to the server so date/time questions need no tool call. */
  readonly timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  constructor(
    private readonly cb: RealtimeCallbacks,
    localSessionId?: string,
  ) {
    this.localSessionId = localSessionId ?? `local_${crypto.randomUUID()}`;
  }

  private emit(
    eventType: DebugEventType,
    extra: Partial<Omit<DebugEvent, "timestamp" | "localSessionId" | "eventType">> = {},
  ) {
    const event: DebugEvent = {
      timestamp: new Date().toISOString(),
      localSessionId: this.localSessionId,
      openaiSessionId: this.openaiSessionId,
      eventType,
      ...extra,
    };
    this.cb.onDebug(event);
    // Mirror into server logs so the whole loop is visible in `railway logs`.
    void fetch("/api/log", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify(event),
    }).catch(() => {});
  }

  /* --------------------------- lifecycle --------------------------- */

  async start() {
    this.connectStartedAt = performance.now();
    this.cb.onStatus("CONNECTING");
    this.emit("SESSION_STARTING");

    // 1. Ephemeral credential from OUR server. The real API key stays there.
    const tokenRes = await fetch("/api/session", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify({ localSessionId: this.localSessionId, timezone: this.timezone }),
    });
    if (!tokenRes.ok) {
      const body = await tokenRes.text();
      throw new Error(`Could not create a Realtime session: ${body}`);
    }
    const session = (await tokenRes.json()) as { clientSecret: string; charter?: CharterView };
    const { clientSecret } = session;
    if (session.charter) this.cb.onCharter?.(session.charter);

    // 2. Microphone.
    try {
      this.mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      throw new Error("Microphone permission was denied. Allow the mic and press Start again.");
    }

    // 3. Peer connection + remote audio playback.
    const pc = new RTCPeerConnection();
    this.pc = pc;

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    this.audioEl = audioEl;
    pc.ontrack = (e) => {
      audioEl.srcObject = e.streams[0];
    };
    pc.addTrack(this.mic.getTracks()[0], this.mic);

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
        this.cb.onError(`WebRTC connection ${pc.connectionState}`);
        this.cb.onStatus("ERROR");
        this.emit("ERROR", { payloadSummary: `webrtc ${pc.connectionState}` });
      }
    };

    // 4. Event data channel.
    const dc = pc.createDataChannel("oai-events");
    this.dc = dc;

    const transport: RealtimeTransport = {
      send: (event) => {
        if (dc.readyState === "open") dc.send(JSON.stringify(event));
      },
    };

    this.loop = new RealtimeFunctionCallLoop({
      transport,
      timeoutMs: ({ args }) => {
        const a = args as any;
        if (a?.reason !== "web_lookup") return 12_000;
        // A deep lookup is allowed the time it was granted; a quick one is not.
        return a?.depth === "deep" ? 70_000 : 20_000;
      },
      execute: async ({ callId, name, args }) => this.executeTool(callId, name, args),
      onDebug: (e) => this.emit(e.eventType, e),
    });

    dc.addEventListener("open", () => {
      const ms = Math.round(performance.now() - this.connectStartedAt);
      this.cb.onStatus("LISTENING");
      this.emit("SESSION_CONNECTED", { durationMs: ms });
      this.requestBriefingAck();
    });
    dc.addEventListener("message", (e) => this.handleServerEvent(e.data));

    // 5. SDP exchange — https://platform.openai.com/docs/api-reference/realtime
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpRes = await fetch(OPENAI_CALLS_URL, {
      method: "POST",
      body: offer.sdp,
      headers: { Authorization: `Bearer ${clientSecret}`, "Content-Type": "application/sdp" },
    });
    if (!sdpRes.ok) {
      throw new Error(`Realtime SDP exchange failed (${sdpRes.status}): ${await sdpRes.text()}`);
    }
    await pc.setRemoteDescription({ type: "answer", sdp: await sdpRes.text() });
  }

  stop() {
    // A pending retry must not fire into a closed session.
    this.awaitingBriefingAck = false;
    this.clearBriefingTimer();
    try {
      this.dc?.close();
      this.pc?.getSenders().forEach((s) => s.track?.stop());
      this.pc?.close();
      this.mic?.getTracks().forEach((t) => t.stop());
      if (this.audioEl) this.audioEl.srcObject = null;
    } catch {
      /* stopping must never throw */
    }
    this.pc = undefined;
    this.dc = undefined;
    this.mic = undefined;
    this.emit("SESSION_STOPPED");
    this.cb.onStatus("DISCONNECTED");

    // Out of band: turn what was just said into topics and observations. The
    // session is already closed, so this costs the user nothing.
    void fetch("/api/summarize", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify({ localSessionId: this.localSessionId }),
    })
      .then((r) => r.json())
      .then((d: any) =>
        this.emit("SESSION_SUMMARIZED", {
          payloadSummary: {
            topics: d?.topicsTouched,
            observations: d?.observationsRecorded,
            promoted: d?.promoted,
          },
        }),
      )
      .catch(() => {});
  }

  /**
   * Ask the model to state the briefing it received, in text rather than audio.
   *
   * Without this, "the model has the charter" is an assumption. The reply is
   * the model's own words, so a briefing that failed to apply shows up as a
   * wrong or missing answer instead of silently behaving oddly later.
   *
   * The exchange is deliberately kept out of the transcript and out of stored
   * turns: it is instrumentation, not conversation, and recording it would put
   * it into memory and summarisation.
   */
  private clearBriefingTimer() {
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = null;
  }

  private requestBriefingAck() {
    if (!this.dc || this.dc.readyState !== "open") return;
    // Never compete with a response that is already running: the API rejects a
    // second one outright, and the turn that matters is the user's.
    if (this.activeResponseId || this.userIsSpeaking) {
      this.awaitingBriefingAck = true;
      return this.retryBriefingAck();
    }
    this.awaitingBriefingAck = true;
    this.briefingAttempts++;
    this.briefingResponseId = null;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              // Asking the model to copy text out of its own instructions reads
              // as an extraction attempt, and it refuses - "I can't provide
              // that" was being displayed as though it were an acknowledgement.
              // Asking it to confirm what it understood gets the same signal.
              text:
                "Readiness check - reply with one line of text and do not speak. " +
                "Give your name, your job in a few words, and how many tools you can use. " +
                "Format: NAME | JOB | N tools",
            },
          ],
        },
      }),
    );
    this.dc.send(JSON.stringify({ type: "response.create", response: { output_modalities: ["text"] } }));

    // A model that never answers must not leave the panel pending forever.
    // Interruption is handled separately, in response.done: greeting the avatar
    // the moment the session opens cancels this response, which is the user
    // behaving normally, not a failure.
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = setTimeout(() => {
      if (!this.awaitingBriefingAck) return;
      this.awaitingBriefingAck = false;
      this.cb.onBriefingAck?.(null);
      this.emit("ERROR", { payloadSummary: "briefing not acknowledged within 15s" });
    }, 15_000);
  }

  /**
   * Ask again after the user's own turn cancelled the check.
   *
   * Speaking first is the common case - the session opens, the user says hello,
   * and semantic VAD cancels whatever was in flight. Retrying keeps the panel
   * honest without ever competing with the person for the turn.
   */
  private retryBriefingAck() {
    if (this.briefingAttempts >= BRIEFING_MAX_ATTEMPTS) {
      this.awaitingBriefingAck = false;
      this.cb.onBriefingAck?.(null);
      this.emit("BRIEFING_SKIPPED", {
        payloadSummary: "the user was speaking, so the briefing check was not run",
      });
      return;
    }
    if (this.briefingTimer) clearTimeout(this.briefingTimer);
    this.briefingTimer = setTimeout(() => {
      if (!this.awaitingBriefingAck) return;
      if (this.userIsSpeaking) return this.retryBriefingAck(); // still talking; wait again
      this.requestBriefingAck();
    }, 1_500);
  }

  /** Text input path — same machinery as speech, useful for debugging without a mic. */
  sendText(text: string) {
    if (!this.dc || this.dc.readyState !== "open") return;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
      }),
    );
    this.dc.send(JSON.stringify({ type: "response.create" }));
    this.recordTurn("user", text);
    this.cb.onTranscript({ role: "user", text, timestamp: new Date().toISOString() });
    this.emit("USER_TURN", { payloadSummary: text });
  }

  /* ---------------------------- internals -------------------------- */

  private async executeTool(callId: string, name: string, args: unknown): Promise<DelegateResult> {
    this.cb.onStatus("TOOL_RUNNING");
    const endpoint = endpointFor(name);

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify({
        localSessionId: this.localSessionId,
        callId,
        name,
        args,
        timezone: this.timezone,
      }),
    });
    if (!res.ok) throw new Error(`Delegate backend returned ${res.status}`);
    const { result, instructions } = (await res.json()) as {
      result: DelegateResult;
      instructions?: string;
    };

    // A preference the user just stated has to bind to THIS session, not the next
    // one. session.update accepts new instructions mid-session, so push them now.
    if (instructions && this.dc?.readyState === "open") {
      this.dc.send(
        JSON.stringify({
          type: "session.update",
          session: { type: "realtime", instructions },
        }),
      );
      this.emit("PREFERENCE_APPLIED", { toolCallId: callId, payloadSummary: args });
    }
    return result;
  }

  private reportUsage(usage: unknown) {
    void fetch("/api/usage", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify({ localSessionId: this.localSessionId, usage }),
    })
      .then((r) => r.json())
      .then((d: any) => {
        if (typeof d?.session?.totalUsd === "number") this.cb.onCost?.(d.session.totalUsd);
        if (typeof d?.cost?.totalUsd === "number") {
          this.emit("COST", {
            payloadSummary: {
              turnUsd: Number(d.cost.totalUsd.toFixed(5)),
              audioUsd: Number(d.cost.audioUsd.toFixed(5)),
              inTokens: d.cost.inputTokens,
              outTokens: d.cost.outputTokens,
              audioTokens: d.cost.audioTokens,
              cached: d.cost.cachedTokens,
            },
          });
        }
      })
      .catch(() => {});
  }

  private recordTurn(role: "user" | "assistant", text: string) {
    void fetch("/api/turn", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...avatarHeader() },
      body: JSON.stringify({ localSessionId: this.localSessionId, role, text }),
    }).catch(() => {});
  }

  /** Realtime server events — https://platform.openai.com/docs/api-reference/realtime */
  private handleServerEvent(raw: string) {
    let event: any;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }

    switch (event.type) {
      case "session.created":
      case "session.updated":
        this.openaiSessionId = event.session?.id ?? this.openaiSessionId;
        break;

      case "input_audio_buffer.speech_started":
        this.userIsSpeaking = true;
        this.emit("SPEECH_STARTED");
        this.cb.onStatus("LISTENING");
        break;

      case "input_audio_buffer.speech_stopped":
        this.userIsSpeaking = false;
        this.emit("SPEECH_STOPPED");
        break;

      case "conversation.item.input_audio_transcription.completed": {
        const text = String(event.transcript ?? "").trim();
        if (text) {
          this.cb.onTranscript({ role: "user", text, timestamp: new Date().toISOString() });
          this.recordTurn("user", text);
          this.emit("USER_TURN", { payloadSummary: text });
        }
        break;
      }

      case "response.created":
        this.activeResponseId = String(event.response?.id ?? "");
        if (this.awaitingBriefingAck && !this.briefingResponseId) {
          this.briefingResponseId = this.activeResponseId;
        }
        this.emit("RESPONSE_STARTED", { payloadSummary: event.response?.id });
        break;

      case "response.output_audio.delta":
      case "response.audio.delta": {
        const id = String(event.response_id ?? "unknown");
        if (!this.audioAnnouncedFor.has(id)) {
          this.audioAnnouncedFor.add(id);
          this.emit("AUDIO_PLAYING", { payloadSummary: id });
          this.cb.onStatus("SPEAKING");
        }
        break;
      }

      case "response.output_text.done":
      case "response.text.done": {
        if (this.awaitingBriefingAck) {
          this.awaitingBriefingAck = false;
          const ack = String(event.text ?? "").trim();
          this.cb.onBriefingAck?.(ack || null);
          this.emit("BRIEFING_ACKNOWLEDGED", { payloadSummary: ack.slice(0, 200) });
        }
        break;
      }

      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done": {
        const text = String(event.transcript ?? "").trim();
        if (text) {
          this.cb.onTranscript({ role: "assistant", text, timestamp: new Date().toISOString() });
          this.recordTurn("assistant", text);
          this.emit("ASSISTANT_TURN", { payloadSummary: text });
        }
        break;
      }

      case "response.done": {
        if (this.activeResponseId === String(event.response?.id ?? "")) this.activeResponseId = null;
        // Token usage arrives per response; this is the only place the browser
        // can see what the voice turn actually cost.
        if (event.response?.usage) this.reportUsage(event.response.usage);
        const status = event.response?.status;
        if (
          this.awaitingBriefingAck &&
          this.briefingResponseId &&
          String(event.response?.id ?? "") === this.briefingResponseId &&
          status !== "completed"
        ) {
          // Cancelled or incomplete: the user started talking over it.
          this.retryBriefingAck();
          break;
        }
        if (status === "failed") {
          const msg = JSON.stringify(event.response?.status_details ?? {});
          this.cb.onError(`Realtime response failed: ${msg}`);
          this.emit("ERROR", { payloadSummary: msg });
        }
        break;
      }

      case "error": {
        const msg = event.error?.message ?? JSON.stringify(event.error ?? event);
        this.cb.onError(msg);
        this.emit("ERROR", { payloadSummary: msg });
        break;
      }

      default:
        break;
    }

    // Tool calls drive the Delegate loop.
    const pending = this.loop?.handleEvent(event) ?? [];
    if (pending.length > 0) {
      void Promise.allSettled(pending).then(() => {
        if (this.pc) this.cb.onStatus("SPEAKING");
      });
    } else if (event.type === "response.done" && this.loop?.pendingCount === 0 && this.pc) {
      this.cb.onStatus("LISTENING");
    }
  }
}

export const TOOL_NAME = DELEGATE_TOOL_NAME;
